import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import type { ProvenanceRecord } from '@arcanum-hq/shared-types';
import { AssumptionCard } from './AssumptionCard';
import {
  usePaginatedAssumptions,
  type RankedAssumption,
} from './usePaginatedAssumptions';
import {
  useSignalFieldResolver,
  type SignalFieldResolverContext,
} from '@/hooks/use-signal-field-resolver';
import { useConversation } from '@/hooks/use-conversations-v2';
import { useModelView } from '@/hooks/use-model-view';
import { useModel } from '@/hooks/use-models-v2';
import { usePublicDeal, useSharedModelView } from '@/hooks/use-public-deal';
import { Skeleton } from '@/components/ui/Skeleton';

// deal-signal-overhaul Phase 4 Session 4 (2026-05-12).
//
// Zone 7 of the Signal page — paginated `<AssumptionCard>` grid. Consumes
// the already-shipped `usePaginatedAssumptions` hook (Phase 6 of
// `agent-ux-optimization`) WITHOUT re-authoring its ranking logic. Session
// 4's contribution is the visual surface + wiring `useSignalFieldResolver`'s
// context → the hook's `(inputDefinitions, inputValues, inputProvenance,
// pageSize)` args by sharing the same TanStack-Query reads the resolver
// already issues internally.
//
// 4-state contract:
//   - loading: 4-or-8 skeleton cards while data hydrates.
//   - error: caught at route level.
//   - empty (`total === 0`): "No assumptions tracked yet" panel.
//   - success: page slice + pagination controls.

export interface AssumptionIntegrityProps {
  context: SignalFieldResolverContext;
  /** 4 (auth Builder Signal, half-width section) or 8 (public Deal Link, full-width). */
  pageSize: 4 | 8;
  className?: string;
}

interface DerivedAssumptionInputs {
  inputValues: Record<string, unknown> | undefined;
  inputProvenance: Record<string, ProvenanceRecord> | undefined;
  modelId: string | undefined;
  isReady: boolean;
}

// Auth-path data plumbing. Reads `useModelView` for `(inputValues,
// provenance)` and `useConversation` for `modelId`. Same hooks
// `useSignalFieldResolver` reads internally — TanStack cache hits keep this
// from doubling network traffic.
function useAuthAssumptions(conversationId: string): DerivedAssumptionInputs {
  const conversation = useConversation(conversationId);
  const modelView = useModelView(conversationId);
  const modelId = conversation.data?.data?.modelId;
  return {
    inputValues: modelView.data?.inputValues,
    inputProvenance: modelView.data?.provenance as
      | Record<string, ProvenanceRecord>
      | undefined,
    modelId,
    isReady: Boolean(conversation.data && modelView.data),
  };
}

// Public-path data plumbing. `usePublicDeal` for the modelId,
// `useSharedModelView` for the frozen inputValues + provenance projection.
function usePublicAssumptions(shareId: string): DerivedAssumptionInputs {
  const publicDeal = usePublicDeal(shareId);
  const sharedModelView = useSharedModelView(shareId);
  return {
    inputValues: sharedModelView.data?.inputValues,
    inputProvenance: sharedModelView.data?.provenance as
      | Record<string, ProvenanceRecord>
      | undefined,
    modelId: publicDeal.data?.modelId,
    isReady: Boolean(publicDeal.data && sharedModelView.data),
  };
}

function useAssumptionDataSources(
  context: SignalFieldResolverContext,
): DerivedAssumptionInputs {
  const isAuth = context.kind === 'conversation';
  // Strict-mode safe: hooks are always called in the same order. The
  // disabled-branch hooks pass a literal placeholder id so TanStack Query's
  // `enabled` gate returns no-data; the resolver does the same trick.
  const auth = useAuthAssumptions(isAuth ? context.conversationId : '');
  const publicDeal = usePublicAssumptions(!isAuth ? context.shareId : '');
  return isAuth ? auth : publicDeal;
}

export function AssumptionIntegrity({
  context,
  pageSize,
  className,
}: AssumptionIntegrityProps) {
  // `deal-signal-overhaul` Phase 5 hotfix (2026-05-13): PREFER
  // `resolver.schema?.inputs` (sources from `sharedModelView.data.schema`
  // on public path — avoids 401-on-/api/models/:id lockup). FALL BACK to
  // `useModel(sources.modelId).data?.data?.inputs` for back-compat with
  // tests + any caller still on the auth-side shape.
  const resolver = useSignalFieldResolver(context);
  const sources = useAssumptionDataSources(context);
  const modelQuery = useModel(sources.modelId ?? null);
  const inputDefinitions =
    resolver.schema?.inputs ?? modelQuery.data?.data?.inputs;
  const isReady = sources.isReady && resolver.isReady && Boolean(inputDefinitions);

  const pagination = usePaginatedAssumptions({
    inputDefinitions,
    inputValues: sources.inputValues,
    inputProvenance: sources.inputProvenance,
    pageSize,
  });

  // Arrow-key pagination on the region root. tabIndex={0} keeps the listener
  // scoped — we never hijack page-wide keys. Mirrors the working pattern
  // from `<SignalOperatingAsset>`'s pre-Session-4 inline implementation.
  const regionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = regionRef.current;
    if (!node) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        pagination.goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        pagination.goPrev();
      }
    };
    node.addEventListener('keydown', onKey);
    return () => node.removeEventListener('keydown', onKey);
  }, [pagination]);

  if (!isReady) {
    return (
      <div
        data-testid="assumption-integrity"
        data-state="loading"
        aria-label="Assumption integrity"
        className={clsx(className)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: pageSize }).map((_, i) => (
            <div
              key={i}
              data-testid="assumption-integrity-skeleton-card"
              className="flex flex-col gap-2 px-4 py-3 border border-panel-border rounded-[3px] bg-panel-bg"
            >
              <Skeleton variant="line" className="w-1/2" />
              <Skeleton variant="line" className="w-3/4 h-5" />
              <Skeleton variant="line" className="w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const startIdx = pagination.total === 0 ? 0 : pagination.page * pageSize + 1;
  const endIdx = Math.min(
    (pagination.page + 1) * pageSize,
    pagination.total,
  );

  return (
    <div
      data-testid="assumption-integrity"
      data-state={pagination.total === 0 ? 'empty' : 'ready'}
      data-page-size={pageSize}
      ref={regionRef}
      tabIndex={0}
      aria-label="Assumption integrity"
      className={clsx(
        'outline-none focus-visible:ring-1 focus-visible:ring-accent rounded-[3px]',
        className,
      )}
    >
      {/* Header — title chip + accent dot + flex-1 hairline + pagination on right */}
      <div className="flex items-center gap-2.5 mb-2">
        <span
          aria-hidden="true"
          className="w-1 h-1 rounded-full bg-accent shrink-0"
          style={{ boxShadow: '0 0 4px rgba(0,200,83,0.6)' }}
        />
        <h2 className="text-[10px] font-mono uppercase tracking-[2px] text-accent font-bold shrink-0">
          Assumption Integrity
        </h2>
        {pagination.total > 0 && (
          <span
            data-testid="assumption-integrity-counter"
            className="text-[9px] font-mono text-text-muted tabular-nums tracking-[0.5px] shrink-0"
          >
            · {startIdx}-{endIdx} of {pagination.total}
          </span>
        )}
        <span className="flex-1 h-px bg-panel-border" />
        {pagination.totalPages > 1 && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              data-testid="assumption-integrity-prev"
              onClick={pagination.goPrev}
              disabled={pagination.page === 0}
              aria-label="Previous assumptions"
              className="w-[18px] h-[18px] border border-accent/40 rounded-[2px] text-accent text-[10px] font-mono leading-none flex items-center justify-center hover:bg-accent/10 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              ‹
            </button>
            <ul
              data-testid="assumption-integrity-dots"
              className="flex items-center gap-1"
            >
              {Array.from({ length: pagination.totalPages }).map((_, i) => (
                <li key={i}>
                  <button
                    type="button"
                    data-testid={`assumption-integrity-dot-${i}`}
                    data-active={i === pagination.page ? 'true' : 'false'}
                    onClick={() => pagination.goPage(i)}
                    aria-label={`Page ${i + 1} of ${pagination.totalPages}`}
                    aria-current={i === pagination.page ? 'page' : undefined}
                    className={clsx(
                      'w-1 h-1 rounded-full transition-colors cursor-pointer',
                      i === pagination.page
                        ? 'bg-accent'
                        : 'bg-text-dim hover:bg-text-muted',
                    )}
                  />
                </li>
              ))}
            </ul>
            <button
              type="button"
              data-testid="assumption-integrity-next"
              onClick={pagination.goNext}
              disabled={pagination.page >= pagination.totalPages - 1}
              aria-label="Next assumptions"
              className="w-[18px] h-[18px] border border-accent/40 rounded-[2px] text-accent text-[10px] font-mono leading-none flex items-center justify-center hover:bg-accent/10 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              ›
            </button>
          </div>
        )}
      </div>
      {pagination.total === 0 ? (
        <p className="text-[10px] font-mono text-text-dim px-3 py-2 border border-panel-border bg-panel-bg">
          No assumptions tracked yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {pagination.current.map((a: RankedAssumption) => (
            <AssumptionCard
              key={a.namedRange}
              namedRange={a.namedRange}
              label={a.label}
              formattedValue={a.formattedValue}
              confidence={a.confidence}
              sensitivityToIRR={a.sensitivityToIRR}
              source={a.source}
              sourceDetail={a.sourceDetail}
              signalDot={null}
            />
          ))}
        </div>
      )}
    </div>
  );
}
